import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  CreditCard,
  CheckCircle2,
  Clock,
  ArrowRight,
  AlertCircle,
  Loader2,
  XCircle,
  RotateCcw,
  Calendar,
  Sparkles,
  Shield,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PLAN_FEATURES = {
  free: [
    "Up to 5 client profiles",
    "Basic service menu (max 5)",
    "Preference sharing links",
    "Community support"
  ],
  solo: [
    "Unlimited client profiles",
    "Full service menu with upsells",
    "Custom intake flow",
    "Inspiration gallery",
    "Analytics dashboard",
    "Payment links (Venmo/CashApp/Zelle)",
    "Client kanban pipeline",
    "Acuity + Tap In integrations",
    "Priority support"
  ],
  team: [
    "Everything in Solo",
    "Up to 5 team members",
    "Shared client directory",
    "Role-based permissions",
    "Team scheduling"
  ],
};

const PLAN_META = {
  free: { name: "Free", color: "bg-gray-100 text-gray-700", accent: "gray", price: "$0", interval: "forever" },
  solo: { name: "Solo", color: "bg-violet-100 text-violet-700", accent: "violet", price: "$11", interval: "/mo" },
  solo_trial: { name: "Solo Trial", color: "bg-emerald-100 text-emerald-700", accent: "emerald", price: "$11", interval: "/mo after trial" },
  team: { name: "Team (coming soon)", color: "bg-blue-50 text-blue-500", accent: "blue", price: "$44", interval: "/mo" },
  luxe: { name: "Luxe (coming soon)", color: "bg-amber-50 text-amber-600", accent: "amber", price: "$99", interval: "/mo" },
  legacy_pilot_free: { name: "Legacy Pilot", color: "bg-blue-100 text-blue-800", accent: "blue", price: "$0", interval: "lifetime" },
  legacy: { name: "Legacy Pilot", color: "bg-blue-100 text-blue-800", accent: "blue", price: "$0", interval: "lifetime" },
};
// Only Free + Solo are open today. Team + Luxe shown but disabled (coming soon).
// Legacy Pilot is invisible to upgrade flow but always visible as the user's current plan.
const OPEN_PLANS = ["free", "solo"];
const COMING_SOON_PLANS = ["team", "luxe"];

const STATUS_CONFIG = {
  active: { label: "Active", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  trialing: { label: "Trial", color: "bg-violet-100 text-violet-700 border-violet-200", icon: Clock },
  cancelled: { label: "Cancelled", color: "bg-amber-100 text-amber-700 border-amber-200", icon: XCircle },
  expired: { label: "Expired", color: "bg-red-100 text-red-700 border-red-200", icon: AlertCircle },
  past_due: { label: "Past Due", color: "bg-orange-100 text-orange-700 border-orange-200", icon: AlertCircle },
  none: { label: "No Plan", color: "bg-gray-100 text-gray-600 border-gray-200", icon: CreditCard },
};

export default function SubscriptionManager({ eventId, getAuthHeaders }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const fetchSub = useCallback(async () => {
    try {
      const res = await fetch(`${API}/subscription/status?event_id=${eventId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setSub(data);
      }
    } catch {
      /* silent */
    }
    setLoading(false);
  }, [eventId, getAuthHeaders]);

  useEffect(() => { fetchSub(); }, [fetchSub]);

  const handleUpgrade = async () => {
    setActionLoading("upgrade");
    try {
      const res = await fetch(`${API}/subscription/stripe-trial`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          plan: "solo",
          billing_cycle: "monthly",
          origin_url: window.location.origin,
          user_id: sub?.user_id || "",
          event_id: eventId,
        })
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || "Could not start checkout");
      }
    } catch {
      toast.error("Connection error");
    }
    setActionLoading(null);
  };

  const handleCancel = async () => {
    setShowCancelDialog(false);
    setActionLoading("cancel");
    try {
      const res = await fetch(`${API}/subscription/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Subscription will not renew.");
        fetchSub();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Could not cancel subscription");
      }
    } catch {
      toast.error("Connection error");
    }
    setActionLoading(null);
  };

  const handleReactivate = async () => {
    setActionLoading("reactivate");
    try {
      const res = await fetch(`${API}/subscription/reactivate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.needs_checkout) {
          navigate("/pricing");
        } else {
          toast.success(data.message || "Subscription reactivated!");
          fetchSub();
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Could not reactivate");
      }
    } catch {
      toast.error("Connection error");
    }
    setActionLoading(null);
  };

  if (loading) {
    return (
      <div className="py-8 text-center" data-testid="subscription-manager-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto mb-2" />
        <p className="text-xs text-gray-400">Loading subscription...</p>
      </div>
    );
  }

  const tier = sub?.plan || sub?.subscription_tier || "free";
  const isLegacyPilotPlan = tier === "legacy_pilot_free" || sub?.account_type === "legacy" || sub?.stripe_bypass === true;
  const effectiveTier = isLegacyPilotPlan ? "legacy" : tier;
  const meta = PLAN_META[effectiveTier] || PLAN_META.free;
  const isTrialing = sub?.status === "trialing" || tier === "solo_trial";
  const isActive = sub?.status === "active" && !sub?.cancel_at_period_end;
  const isCancelling = sub?.cancel_at_period_end === true;
  const isExpired = sub?.status === "expired" || sub?.status === "none";
  const isLegacy = effectiveTier === "legacy" || sub?.account_type === "legacy";
  const trialEndsAt = sub?.trial_ends_at;
  const daysLeft = trialEndsAt ? Math.max(0, Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24))) : null;
  const periodEnd = sub?.current_period_end;

  const statusKey = isCancelling ? "cancelled" : (sub?.status || "none");
  const statusCfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.none;
  const StatusIcon = statusCfg.icon;
  const features = PLAN_FEATURES[effectiveTier === "legacy" ? "solo" : effectiveTier] || PLAN_FEATURES.free;
  const canCancel = (isActive || isTrialing) && !isLegacy && effectiveTier !== "free";
  const canReactivate = isCancelling;
  const canUpgrade = (effectiveTier === "free" || isExpired) && !isLegacy;

  return (
    <div className="space-y-5" data-testid="subscription-manager">
      {/* Plan Header Card */}
      <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-gray-50 to-white p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              effectiveTier === "legacy" ? "bg-amber-100" :
              effectiveTier === "solo" || isTrialing ? "bg-violet-100" :
              effectiveTier === "team" ? "bg-blue-100" : "bg-gray-100"
            }`}>
              {isLegacy ? <Shield className="w-6 h-6 text-amber-600" /> :
               effectiveTier === "solo" || isTrialing ? <Sparkles className="w-6 h-6 text-violet-600" /> :
               <CreditCard className="w-6 h-6 text-gray-500" />}
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h3 className="text-base font-semibold text-gray-900">{meta.name} Plan</h3>
                <Badge className={`${statusCfg.color} border text-[10px] px-1.5 py-0`} data-testid="sub-status-badge">
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusCfg.label}
                </Badge>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                {meta.price}{meta.interval}
                {sub?.billing_cycle === "annual" && effectiveTier !== "free" && effectiveTier !== "legacy" && " (annual billing)"}
              </p>
            </div>
          </div>
        </div>

        {/* Trial Countdown */}
        {isTrialing && daysLeft !== null && (
          <div className={`mt-4 flex items-center gap-3 p-3 rounded-lg border ${
            daysLeft > 14 ? "bg-emerald-50 border-emerald-200" :
            daysLeft > 3 ? "bg-amber-50 border-amber-200" :
            "bg-red-50 border-red-200"
          }`} data-testid="trial-countdown">
            <Clock className={`w-4 h-4 shrink-0 ${
              daysLeft > 14 ? "text-emerald-600" : daysLeft > 3 ? "text-amber-600" : "text-red-600"
            }`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${
                daysLeft > 14 ? "text-emerald-700" : daysLeft > 3 ? "text-amber-700" : "text-red-700"
              }`}>
                {daysLeft} day{daysLeft !== 1 ? "s" : ""} left in your trial
              </p>
              {trialEndsAt && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Ends {new Date(trialEndsAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Cancellation Notice */}
        {isCancelling && (
          <div className="mt-4 flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="cancel-notice">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-700">Cancellation scheduled</p>
              <p className="text-xs text-amber-600 mt-0.5">
                Full access until {periodEnd ? new Date(periodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "end of billing period"}. Your plan will not renew.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={handleReactivate}
              disabled={actionLoading === "reactivate"}
              data-testid="reactivate-btn"
            >
              {actionLoading === "reactivate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
              Keep Plan
            </Button>
          </div>
        )}

        {/* Legacy Status */}
        {isLegacy && (
          <div className="mt-4 flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="legacy-notice">
            <Shield className="w-4 h-4 text-blue-700 shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800">Legacy Pilot</p>
              <p className="text-xs text-gray-500 mt-0.5">All Phygital features unlocked, free for life.</p>
            </div>
          </div>
        )}

        {/* Expired Notice */}
        {isExpired && !isLegacy && effectiveTier !== "free" && (
          <div className="mt-4 flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="expired-notice">
            <XCircle className="w-4 h-4 text-red-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-700">Subscription expired</p>
              <p className="text-xs text-red-600 mt-0.5">Some features are limited. Resubscribe to restore full access.</p>
            </div>
          </div>
        )}

        {/* Billing Period */}
        {periodEnd && !isCancelling && !isExpired && (
          <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
            <Calendar className="w-3.5 h-3.5" />
            <span>Next billing: {new Date(periodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          </div>
        )}
      </div>

      {/* Features List */}
      <div className="px-1">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          {isLegacy ? "All Features Unlocked" : `${meta.name} Features`}
        </p>
        <div className="grid grid-cols-1 gap-1.5">
          {features.map((feature, i) => (
            <div key={i} className="flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span className="text-xs text-gray-600">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Actions */}
      <div className="space-y-2">
        {/* Upgrade CTA */}
        {canUpgrade && (
          <Button
            onClick={handleUpgrade}
            disabled={actionLoading === "upgrade"}
            className="w-full rounded-xl bg-gray-900 hover:bg-gray-800 text-white h-11"
            data-testid="upgrade-btn"
          >
            {actionLoading === "upgrade" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="w-4 h-4 mr-2" />
            )}
            Upgrade to Solo -- $11/mo (60-day free trial)
          </Button>
        )}

        {/* View Full Subscription Page */}
        <Button
          variant="outline"
          className="w-full rounded-xl h-10"
          onClick={() => navigate("/subscription")}
          data-testid="manage-subscription-btn"
        >
          <ExternalLink className="w-3.5 h-3.5 mr-2" />
          Full Subscription Details
        </Button>

        {/* Cancel */}
        {canCancel && (
          <Button
            variant="ghost"
            className="w-full rounded-xl h-9 text-gray-400 hover:text-red-600 hover:bg-red-50 text-xs"
            onClick={() => setShowCancelDialog(true)}
            disabled={actionLoading === "cancel"}
            data-testid="cancel-subscription-btn"
          >
            {actionLoading === "cancel" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Cancel subscription
          </Button>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Your subscription will not renew at the end of the current billing period. You will keep full access to all features until then. No refund will be issued.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-dismiss">Keep Plan</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="cancel-dialog-confirm"
            >
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
